import { zodResolver } from '@hookform/resolvers/zod'
import { Mail } from 'lucide-react'
import { useForm } from 'react-hook-form'
import type { Path } from 'react-hook-form'
import { Button, PasswordField, TextField } from '@shared/design-system/ui'
import { AppError } from '@shared/api/error'
import { useSession } from '@shared/lib/auth/session-store'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { toast } from '@shared/lib/toast/toast-store'
import { applyApiFieldErrors } from '@shared/lib/forms/api-field-errors'
import { useQueryClient } from '@tanstack/react-query'
import { currentUserQuery } from '@entities/user'
import { LoginInputSchema } from '../api/auth.contract'
import { useLoginMutation } from '../api/auth.mutations'
import type { LoginInput } from '../api/auth.contract'
import { getRedirectTarget, navigateTo } from './auth-routing'
import { actions, form, inline, link, mutedText } from './auth.css'
import { AuthLayout } from './AuthLayout'

function isInvalidCredentialsError(error: unknown) {
  return (
    error instanceof AppError &&
    error.status === 401 &&
    (error.code === 'InvalidCredentialsError' || error.code === 'InvalidCredentials')
  )
}

export function LoginPage() {
  const queryClient = useQueryClient()
  const setSession = useSession((state) => state.setSession)
  const setUser = useSession((state) => state.setUser)
  const clearSession = useSession((state) => state.clear)
  const loginMutation = useLoginMutation()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(LoginInputSchema),
    defaultValues: { email: '', password: '' },
  })
  const hasInvalidCredentialsError =
    errors.email?.type === 'credentials' || errors.password?.type === 'credentials'
  const emailError = errors.email?.type === 'credentials' ? undefined : errors.email?.message

  const onSubmit = handleSubmit(async (values) => {
    try {
      const response = await loginMutation.mutateAsync(values)
      setSession(response.token, response.user)
      try {
        setUser(await queryClient.fetchQuery({ ...currentUserQuery(), staleTime: 0 }))
      } catch (error) {
        clearSession()
        throw error
      }
      toast.success('Sessão iniciada.')
      navigateTo(getRedirectTarget('/'))
    } catch (error) {
      const hasFieldErrors = applyApiFieldErrors(error, (field, fieldError) =>
        setError(field as Path<LoginInput>, fieldError),
      )
      if (hasFieldErrors) {
        toast.error({
          title: 'Revise os campos',
          description: 'Confira os campos destacados antes de entrar.',
        })
        return
      }

      if (isInvalidCredentialsError(error)) {
        setError('email', { type: 'credentials' })
        setError('password', { type: 'credentials', message: 'E-mail ou senha inválidos.' })
        toast.error({
          title: 'Revise os campos',
          description: 'Confira o e-mail e a senha antes de entrar.',
        })
        return
      }

      toast.error(
        getUserFacingError(
          error,
          error instanceof AppError && error.status === 429
            ? {}
            : {
                title: 'Não foi possível entrar',
                fallback: 'Confira seus dados e tente novamente.',
              },
        ),
      )
    }
  })

  return (
    <AuthLayout
      title="Entrar na conta"
      subtitle="Acesse seu painel para acompanhar vendas, produtos e recebimentos."
    >
      <form data-testid="login-form" className={form} onSubmit={(event) => void onSubmit(event)}>
        <TextField
          label="E-mail"
          data-testid="login-email"
          type="email"
          autoComplete="email"
          leftIcon={<Mail size={17} />}
          status={hasInvalidCredentialsError ? 'error' : undefined}
          error={emailError}
          {...register('email')}
        />
        <PasswordField
          label="Senha"
          data-testid="login-password"
          autoComplete="current-password"
          status={hasInvalidCredentialsError ? 'error' : undefined}
          error={errors.password?.message}
          {...register('password')}
        />
        <div className={inline}>
          <span className={mutedText}>Novo por aqui?</span>
          <a className={link} href="/cadastro">
            Criar conta
          </a>
        </div>
        <div className={actions}>
          <Button
            data-testid="login-submit-btn"
            type="submit"
            fullWidth
            disabled={loginMutation.isPending}
          >
            {loginMutation.isPending ? 'Entrando...' : 'Entrar'}
          </Button>
          <a className={link} href="/esqueci-senha">
            Esqueci minha senha
          </a>
        </div>
      </form>
    </AuthLayout>
  )
}
